import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle } from 'lucide-react';
import { webSocketService } from '@/services/websocket.service';
import { queueApiService, type Queue, type QueueEntry } from '@/services/queueApi.service';
import { getStatusDisplay, getStatusContextualMessage } from '@/utils/statusMapping';
import { SimultaneousAttendanceDisplay } from './SimultaneousAttendanceDisplay';
import { DynamicFieldList } from './DynamicField';
import { CustomFieldConfig } from './CustomFieldsBuilder';
import { useCustomFieldsConfig, useDynamicSchema } from '@/hooks/useCustomFields';
import { useOptinsConfig } from '@/hooks/useOptins';
import { OptinCheckbox } from './OptinCheckbox';
import { OptinType } from '@/services/queueApi.service';
import { Form } from '@/components/ui/form';
import { ModalPortal } from '@/components/ds/ModalPortal';
import { useCorDoEvento } from '@/components/ds/IlhaEscura';

interface ParticipantQueueViewProps {
  queueId?: string | null;
  entryId?: string | null;
  userId?: string;
  onLeaveQueue?: () => void;
}

interface ConfiguracaoSolucao {
  id: string;
  corPrincipal: string;
  corTexto: string;
  logo: string;
  imagemFundo: string;
  termosUso: string;
  modoAgendamento?: boolean;
}

interface RegistrationData {
  name: string;
  phone: string;
  email?: string;
  notificationPreferences: {
    whatsapp: { enabled: boolean; frequency: string };
    email: { enabled: boolean };
    push: { enabled: boolean; frequency: string };
  };
  customFields?: Record<string, any>;
}

type ViewState = 'loading' | 'queue-info' | 'registration' | 'tracking' | 'error';

export default function ParticipantQueueView({ 
  queueId, 
  entryId,
  userId,
  onLeaveQueue 
}: ParticipantQueueViewProps) {
  const navigate = useNavigate();
  const [queue, setQueue] = useState<Queue | null>(null);
  const [position, setPosition] = useState<QueueEntry | null>(null);
  const [viewState, setViewState] = useState<ViewState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const [showConfirmCancelModal, setShowConfirmCancelModal] = useState(false);
  const [hasLeftQueue, setHasLeftQueue] = useState(false);
  const [isUpdatingPosition, setIsUpdatingPosition] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  // Removed unused state
  const [, setQrCodeData] = useState<string>('');
  const [trackingUrl, setTrackingUrl] = useState<string>('');
  const [config, setConfig] = useState<ConfiguracaoSolucao>({
    id: 'default',
    corPrincipal: '#4369F5',
    corTexto: '#FFFFFF',
    logo: '/logo.png',
    imagemFundo: '/idVIsual.webp',
    termosUso: ''
  });
  useCorDoEvento(config.corPrincipal);
  const [registrationData] = useState<RegistrationData>({
    name: '',
    phone: '',
    email: '',
    notificationPreferences: {
      whatsapp: { enabled: true, frequency: 'important' },
      email: { enabled: false },
      push: { enabled: true, frequency: 'critical' }
    },
    customFields: {}
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setIsNearTurn] = useState(false);
  const [, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('disconnected');
  const [optinValues, setOptinValues] = useState<Record<string, boolean>>({});

  // Load custom fields configuration for the queue
  console.log('[ParticipantQueueView] Calling useCustomFieldsConfig with queueId:', queueId);
  const { data: customFieldsConfig } = useCustomFieldsConfig(queueId || '');
  const customFields = (customFieldsConfig || []) as CustomFieldConfig[];
  console.log('[ParticipantQueueView] Custom fields loaded:', customFields);

  // Load opt-ins configuration for the queue
  const { data: optinsConfigData } = useOptinsConfig(queueId || '');
  const optinsConfig = optinsConfigData ?? [];

  // Build dynamic schema based on custom fields
  const dynamicSchema = useDynamicSchema(customFields);
  console.log('[ParticipantQueueView] Dynamic schema built:', dynamicSchema);

  // Initialize react-hook-form with dynamic schema
  const defaultValues: any = {
    name: '',
    phone: '',
    email: '',
    customFields: {}
  };

  // Add custom fields to the nested customFields object
  customFields.forEach(field => {
    let defaultValue = field.defaultValue || '';
    if (field.fieldType === 'checkbox') {
      defaultValue = false;
    } else if (field.fieldType === 'number') {
      defaultValue = '';
    } else if (field.fieldType === 'date') {
      defaultValue = ''; // Empty string for date fields
    }
    defaultValues.customFields[field.fieldKey] = defaultValue;
  });

  console.log('[ParticipantQueueView] Form default values:', defaultValues);

  const form = useForm({
    resolver: zodResolver(dynamicSchema),
    mode: 'onChange', // Validate on change to update isValid state
    defaultValues: defaultValues
  });

  // Watch customFields for conditional visibility evaluation
  const watchedCustomFields = form.watch('customFields') ?? {};

  // Initialize optinValues when optinsConfig loads
  useEffect(() => {
    if (optinsConfigData && optinsConfigData.length > 0) {
      setOptinValues((prev) => {
        const initial: Record<string, boolean> = {};
        optinsConfigData.forEach((o) => {
          initial[o.key] = prev[o.key] ?? false;
        });
        return initial;
      });
    }
  }, [optinsConfigData]);

  // Debug form state changes
  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      console.log('[Form Debug] Field changed:', {
        fieldName: name,
        changeType: type,
        currentValues: value,
        isValid: form.formState.isValid,
        isDirty: form.formState.isDirty,
        errors: form.formState.errors,
        dirtyFields: form.formState.dirtyFields,
        // Check specifically for customFields
        customFieldsInValue: value.customFields || 'Not found',
        allKeys: Object.keys(value),
      });
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const loadPositionByEntryId = async (entryIdParam: string) => {
    try {
      console.log('Loading position by entryId:', entryIdParam);
      
      // Get entry details by ID
      const entryData = await queueApiService.getEntryById(entryIdParam);
      
      console.log('Entry data received:', entryData);
      
      if (!entryData) {
        setError('Entrada não encontrada');
        setViewState('error');
        return;
      }
      
      // Set position data with proper typing
      setPosition({
        ...entryData,
        estimatedWaitTime: entryData.estimatedWaitTime || 0
      });
      setQrCodeData(entryData.qrCode || '');
      
      // Generate tracking URL
      const url = `${window.location.origin}/acompanhar/${entryIdParam}`;
      setTrackingUrl(url);
      
      // Get queue information
      const queueIdFromEntry = entryData.queueId;
      if (queueIdFromEntry) {
        try {
          const queueData = await queueApiService.getPublicQueueInfo(queueIdFromEntry);
          setQueue(queueData);
          
          console.log('Queue data loaded:', queueData);
          console.log('EventId from queue:', queueData.eventId);
          
          // Load configuration with eventId
          try {
            if (queueData.eventId) {
              console.log('Fetching configuration for eventId:', queueData.eventId);
              const configData = await queueApiService.getConfiguracaoSolucao(queueData.eventId);
              console.log('Configuration loaded:', configData);
              setConfig(configData);
            } else {
              console.warn('No eventId found in queue data, using default configuration');
            }
          } catch (err) {
            console.error('Error loading configuration:', err);
            console.warn('Using default configuration');
          }
          
          // Setup WebSocket for real-time updates
          setupWebSocket(queueIdFromEntry);
        } catch (err) {
          console.error('Error loading queue info:', err);
        }
      }
      
      // Store in session for recovery
      if (queueIdFromEntry) {
        sessionStorage.setItem(`queue_entry_${queueIdFromEntry}`, JSON.stringify({
          entryId: entryIdParam,
          phone: entryData.participantPhone,
          joinedAt: entryData.joinedAt,
          qrCode: entryData.qrCode,
          trackingUrl: url
        }));
      }
      
      setViewState('tracking');
    } catch (err: any) {
      console.error('Error loading entry by ID:', err);
      setError(err.response?.data?.message || 'Não foi possível carregar sua posição');
      setViewState('error');
    }
  };

  const loadPositionByUserId = async (queueIdParam: string, userIdParam: string) => {
    try {
      console.log('Loading position by userId:', userIdParam);
      
      // Get queue information first
      const queueData = await queueApiService.getPublicQueueInfo(queueIdParam);
      setQueue(queueData);
      
      // Load configuration with eventId
      try {
        if (queueData.eventId) {
          console.log('Fetching configuration for eventId:', queueData.eventId);
          const configData = await queueApiService.getConfiguracaoSolucao(queueData.eventId);
          console.log('Configuration loaded:', configData);
          setConfig(configData);
        } else {
          console.warn('No eventId found in queue data, using default configuration');
        }
      } catch (err) {
        console.error('Error loading configuration:', err);
        console.warn('Using default configuration');
      }
      
      // Get user position in queue
      const positionData = await queueApiService.getPosition(queueIdParam);
      
      if (positionData) {
        setPosition(positionData);
        setQrCodeData(positionData.qrCode || '');
        
        // Setup WebSocket
        setupWebSocket(queueIdParam);
        setViewState('tracking');
      } else {
        // User not in queue, show queue info
        setViewState('queue-info');
      }
    } catch (err: any) {
      console.error('Error loading position by userId:', err);
      
      // If user not found in queue, show queue info
      if (err.response?.status === 404) {
        setViewState('queue-info');
      } else {
        setError(err.response?.data?.message || 'Erro ao carregar posição');
        setViewState('error');
      }
    }
  };

  useEffect(() => {
    initializeView();
  }, [queueId, entryId, userId]);

  const initializeView = async () => {
    try {
      setViewState('loading');
      setError(null);
      
      // Case 1: Direct entry access via entryId
      if (entryId) {
        await loadPositionByEntryId(entryId);
        return;
      }
      
      // Case 2: Authenticated user access
      if (userId && queueId) {
        await loadPositionByUserId(queueId, userId);
        return;
      }
      
      // Case 3: Queue-based access (new registration or session recovery)
      if (!queueId) {
        setError('ID da fila não fornecido');
        setViewState('error');
        return;
      }
      
      // First, try to get public queue information
      const queueData = await queueApiService.getPublicQueueInfo(queueId);
      setQueue(queueData);
      
      console.log('Queue data loaded:', queueData);
      console.log('EventId from queue:', queueData.eventId);
      
      // Load configuration with eventId
      try {
        if (queueData.eventId) {
          console.log('Fetching configuration for eventId:', queueData.eventId);
          const configData = await queueApiService.getConfiguracaoSolucao(queueData.eventId);
          console.log('Configuration loaded:', configData);
          setConfig(configData);
        } else {
          console.warn('No eventId found in queue data, using default configuration');
        }
      } catch (err) {
        console.error('Error loading configuration:', err);
        console.warn('Using default configuration');
      }
      
      // Check if user is already in queue (session-based for public participants)
      // Note: We don't have authenticated users in this participant view
      {
        // Check session storage for public participants
        const storedEntry = sessionStorage.getItem(`queue_entry_${queueId}`);
        if (storedEntry) {
          try {
            const entryInfo = JSON.parse(storedEntry);
            // Try to get position using the stored temp user ID
            const tempUserId = generateTempUserId(entryInfo.phone);
            const positionData = await queueApiService.getPositionPublic(queueId, tempUserId);
            if (positionData) {
              setPosition({
                id: positionData.id || positionData._id || '',
                position: positionData.position,
                status: positionData.status,
                joinedAt: entryInfo.joinedAt,
                qrCode: entryInfo.qrCode || '',
                estimatedWaitTime: positionData.estimatedWaitTime,
                queueId: queueId
              } as QueueEntry);
              setQrCodeData(entryInfo.qrCode || '');
              // Restore tracking URL from session
              if (entryInfo.trackingUrl) {
                setTrackingUrl(entryInfo.trackingUrl);
              } else if (entryInfo.entryId) {
                const url = `${window.location.origin}/acompanhar/${entryInfo.entryId}`;
                setTrackingUrl(url);
              }
              setupWebSocket(queueId);
              setViewState('tracking');
              return;
            }
          } catch (err) {
            // Clear invalid session data if position not found
            sessionStorage.removeItem(`queue_entry_${queueId}`);
          }
        }
      }
      
      // Show queue info for registration
      setViewState('queue-info');
    } catch (err) {
      console.error('Error initializing view:', err);
      setError('Não foi possível carregar informações da fila');
      setViewState('error');
    }
  };

  const setupWebSocket = (queueIdParam?: string) => {
    try {
      console.log('Setting up WebSocket for queue:', queueIdParam || queueId);
      
      // Connect WebSocket for all users (authenticated and public)
      webSocketService.connect();
      
      // Use provided queueId or fall back to prop
      const targetQueueId = queueIdParam || queueId;
      
      // Only subscribe to queue if we have a valid queueId
      if (targetQueueId) {
        console.log('Subscribing to queue:', targetQueueId);
        webSocketService.subscribeToQueue(targetQueueId);
        webSocketService.on('position:update', handlePositionUpdate);
        webSocketService.on('your:turn:soon', handleTurnSoon);
        webSocketService.on('your:turn:now', handleTurnNow);
        webSocketService.on('connection:status', handleConnectionStatus);
        webSocketService.on('your:status:changed', handleStatusChanged);
        webSocketService.on('status:changed', handleGlobalStatusChanged);
        
        // Listen for capacity updates
        webSocketService.on('queue:capacity:reached', handleCapacityReached);
        webSocketService.on('queue:capacity:available', handleCapacityAvailable);
        webSocketService.on('queue:capacity:updated', handleCapacityUpdated);
        webSocketService.on('queue:entry:joined', handleEntryJoined);
        webSocketService.on('queue:entry:left', handleEntryLeft);
        
        // Only setup polling if WebSocket is not connected
        // Polling will be started if connection fails later
        if (!webSocketService.isConnected()) {
          console.log('WebSocket not connected, setting up polling fallback');
          setupPollingFallback(targetQueueId);
        }
      }
    } catch (err) {
      console.warn('WebSocket connection failed:', err);
      // Setup polling fallback
      const effectiveQueueId = queueIdParam || queueId;
      if (effectiveQueueId) {
        setupPollingFallback(effectiveQueueId);
      }
    }
  };

  const setupPollingFallback = (targetQueueId: string) => {
    // Don't setup polling if already running
    if ((window as any).pollInterval) {
      console.log('Polling already running, skipping setup');
      return;
    }
    
    // Poll for position updates every 10 seconds as fallback
    const pollInterval = setInterval(async () => {
      if (entryId || (userId && targetQueueId)) {
        try {
          console.log('Polling for position update...');
          
          let positionData = null;
          if (entryId) {
            positionData = await queueApiService.getEntryById(entryId);
          } else if (targetQueueId) {
            positionData = await queueApiService.getPosition(targetQueueId);
          }
          
          if (positionData && position) {
            // Only update if position changed to avoid unnecessary re-renders
            if (positionData.position !== position.position || 
                positionData.estimatedWaitTime !== position.estimatedWaitTime) {
              console.log('Position update via polling:', {
                position: positionData.position,
                estimatedWaitTime: positionData.estimatedWaitTime
              });
              setPosition({
                ...position,
                ...positionData,
                estimatedWaitTime: positionData.estimatedWaitTime || 0
              });
            }
          }
        } catch (err) {
          console.warn('Polling failed:', err);
        }
      }
    }, 10000);

    // Store interval for cleanup
    (window as any).pollInterval = pollInterval;
  };

  const handleStatusChanged = (data: any) => {
    console.log('Personal status change received:', data);
    
    if (position) {
      setPosition(prev => prev ? {
        ...prev,
        status: data.newStatus,
        position: data.newStatus === 'completed' ? 0 : prev.position
      } : null);
    }
  };

  const handleGlobalStatusChanged = (data: any) => {
    console.log('Global status change received:', data);
    
    const currentEntryId = entryId || position?.id || position?._id;
    const currentUserId = userId || position?.userId;
    
    // Check if this status change is for the current entry
    const isCurrentEntry = (currentEntryId && data.entryId === currentEntryId) ||
                          (currentUserId && data.userId === currentUserId);
    
    if (isCurrentEntry && position) {
      console.log('Updating status for current entry:', data.newStatus);
      
      // If status changed to abandoned, show exit modal
      if (data.newStatus === 'abandoned') {
        setHasLeftQueue(true);
        setShowExitModal(true);
        // Clear session storage
        const actualQueueId = queueId || position?.queueId || queue?._id;
        if (actualQueueId) {
          sessionStorage.removeItem(`queue_entry_${actualQueueId}`);
        }
      }
      
      setPosition(prev => prev ? {
        ...prev,
        status: data.newStatus,
        position: data.newStatus === 'completed' || data.newStatus === 'abandoned' ? 0 : (data.position ?? prev.position)
      } : null);
      
      // If someone completed, we might need to refresh positions
      if (data.newStatus === 'completed') {
        // Trigger a position refresh after a short delay
        setTimeout(async () => {
          if (entryId) {
            const updated = await queueApiService.getEntryById(entryId);
            if (updated) {
              console.log('Refreshed position after completion:', updated);
              setPosition(prev => ({
                ...prev,
                ...updated,
                estimatedWaitTime: updated.estimatedWaitTime || 0
              }));
            }
          }
        }, 1000);
      }
    } else if (data.newStatus === 'completed' && position?.status === 'waiting') {
      // Someone else completed, our position might have changed
      console.log('Someone else completed, refreshing our position...');
      setTimeout(async () => {
        if (entryId) {
          const updated = await queueApiService.getEntryById(entryId);
          if (updated) {
            console.log('Updated position after someone else completed:', updated);
            setPosition(prev => ({
              ...prev,
              ...updated,
              estimatedWaitTime: updated.estimatedWaitTime || 0
            }));
          }
        }
      }, 1000);
    }
  };

  const handleConnectionStatus = (data: { connected: boolean }) => {
    console.log('WebSocket connection status:', data.connected);
    setConnectionStatus(data.connected ? 'connected' : 'disconnected');
    
    // If WebSocket disconnected, start polling as fallback
    if (!data.connected && (queueId || entryId)) {
      console.log('WebSocket disconnected, starting polling fallback');
      const targetQueueId = position?.queueId || queueId;
      if (targetQueueId && !(window as any).pollInterval) {
        setupPollingFallback(targetQueueId);
      }
    } else if (data.connected && (window as any).pollInterval) {
      // If WebSocket reconnected, stop polling
      console.log('WebSocket reconnected, stopping polling');
      clearInterval((window as any).pollInterval);
      delete (window as any).pollInterval;
    }
  };

  const handleCapacityReached = (data: any) => {
    console.log('Queue capacity reached:', data);
    if (queue) {
      setQueue(prev => prev ? {
        ...prev,
        metrics: {
          ...prev.metrics,
          currentSize: data.max || prev.metrics?.currentSize || 0
        }
      } as Queue : null);
    }
  };

  const handleCapacityAvailable = (data: any) => {
    console.log('Queue capacity available:', data);
    // Refresh queue data to show updated capacity
    if (queueId || queue?._id) {
      queueApiService.getPublicQueueInfo(queueId || queue?._id || '').then(queueData => {
        setQueue(queueData);
      }).catch(console.error);
    }
  };

  const handleCapacityUpdated = (data: any) => {
    console.log('Queue capacity updated:', data);
    if (queue && data.capacity) {
      setQueue(prev => prev ? {
        ...prev,
        settings: {
          ...prev.settings,
          maxSize: data.capacity.max
        },
        metrics: {
          ...prev.metrics,
          currentSize: data.capacity.current
        }
      } as Queue : null);
    }
  };

  const handleEntryJoined = (data: any) => {
    console.log('Entry joined queue:', data);
    if (queue && data.capacity) {
      setQueue(prev => prev ? {
        ...prev,
        metrics: {
          ...prev.metrics,
          currentSize: data.capacity.current
        }
      } as Queue : null);
    }
  };

  const handleEntryLeft = (data: any) => {
    console.log('Entry left queue:', data);
    if (queue && data.capacity) {
      setQueue(prev => prev ? {
        ...prev,
        metrics: {
          ...prev.metrics,
          currentSize: data.capacity.current
        }
      } as Queue : null);
    }
  };

  useEffect(() => {
    return () => {
      // Cleanup WebSocket subscriptions
      if (queueId) {
        webSocketService.unsubscribeFromQueue(queueId);
        webSocketService.off('position:update', handlePositionUpdate);
        webSocketService.off('your:turn:soon', handleTurnSoon);
        webSocketService.off('your:turn:now', handleTurnNow);
        webSocketService.off('your:status:changed', handleStatusChanged);
        webSocketService.off('status:changed', handleGlobalStatusChanged);
        webSocketService.off('connection:status', handleConnectionStatus);
        webSocketService.off('queue:capacity:reached', handleCapacityReached);
        webSocketService.off('queue:capacity:available', handleCapacityAvailable);
        webSocketService.off('queue:capacity:updated', handleCapacityUpdated);
        webSocketService.off('queue:entry:joined', handleEntryJoined);
        webSocketService.off('queue:entry:left', handleEntryLeft);
      }
      
      // Clear any polling intervals
      if ((window as any).pollInterval) {
        clearInterval((window as any).pollInterval);
      }
    };
  }, [queueId]);

  const handleJoinQueue = () => {
    setViewState('registration');
  };

  const handleRegistrationSubmit = async (formData: any) => {
    if (!queueId) {
      setError('ID da fila não encontrado. Por favor, recarregue a página.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      console.log('[handleRegistrationSubmit] Form data received:', formData);
      console.log('[handleRegistrationSubmit] All form data keys:', Object.keys(formData));

      // Format phone to international format (+55 for Brazil)
      const cleanPhone = formData.phone.replace(/\D/g, '');
      const formattedPhone = cleanPhone.startsWith('55') ? `+${cleanPhone}` : `+55${cleanPhone}`;

      // Extract custom fields from form data
      const customFieldsData: Record<string, any> = {};

      // Check if there's a nested customFields object
      if (formData.customFields && typeof formData.customFields === 'object') {
        console.log('[handleRegistrationSubmit] Found nested customFields:', formData.customFields);
        Object.assign(customFieldsData, formData.customFields);
      }

      // Also check for fields with customFields. prefix
      Object.keys(formData).forEach(key => {
        if (key.startsWith('customFields.')) {
          const fieldKey = key.replace('customFields.', '');
          customFieldsData[fieldKey] = formData[key];
          console.log(`[handleRegistrationSubmit] Custom field extracted from key: ${fieldKey} = ${formData[key]}`);
        }
      });

      // Trim repeat group arrays to match the count field value
      // (user may have decreased the count after filling sections)
      customFields.forEach((field) => {
        if (field.repeatGroup) {
          const count = Math.max(0, Math.min(Number(customFieldsData[field.fieldKey]) || 0, field.repeatGroup.maxRepetitions ?? 20));
          const groupArray = customFieldsData[field.repeatGroup.groupKey];
          if (Array.isArray(groupArray) && groupArray.length > count) {
            customFieldsData[field.repeatGroup.groupKey] = groupArray.slice(0, count);
          }
        }
      });

      console.log('[handleRegistrationSubmit] Final custom fields to send:', customFieldsData);

      const entryData = await queueApiService.joinQueue(queueId, {
        name: sanitizeInput(formData.name),
        phone: formattedPhone, // Send in international format
        email: formData.email ? sanitizeInput(formData.email) : '',
        notificationPreferences: registrationData.notificationPreferences,
        customFields: customFieldsData,
        optins: optinValues,
      });
      
      setPosition(entryData);
      setQrCodeData(entryData.qrCode || '');
      
      // Generate tracking URL with entry ID
      const entryId = entryData.id || entryData._id;
      const url = `${window.location.origin}/acompanhar/${entryId}`;
      setTrackingUrl(url);
      
      // Make sure we have queue data - if not, fetch it
      if (!queue) {
        try {
          const queueData = await queueApiService.getPublicQueueInfo(queueId);
          setQueue(queueData);
        } catch (err) {
          console.error('Error fetching queue info:', err);
        }
      }
      
      // Store entry info in session storage for position tracking
      sessionStorage.setItem(`queue_entry_${queueId}`, JSON.stringify({
        entryId: entryData.id || entryData._id,
        phone: registrationData.phone,
        joinedAt: entryData.joinedAt,
        qrCode: entryData.qrCode,
        trackingUrl: url
      }));
      
      setupWebSocket(queueId);
      
      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
      
      // Redirect to tracking page with entryId
      const redirectEntryId = entryData.id || entryData._id;
      navigate(`/acompanhar/${redirectEntryId}`, { replace: true });
      
      // Set view state after navigation (for display during redirect)
      setViewState('tracking');
      
    } catch (err: any) {
      console.error('Error joining queue:', err);
      setError(err.response?.data?.message || 'Não foi possível entrar na fila');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sanitizeInput = (input: string): string => {
    // Basic XSS prevention - remove HTML tags and dangerous characters
    return input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
                .replace(/<[^>]+>/g, '')
                .trim();
  };

  const loadPosition = async () => {
    // Get the actual queueId from position, queue state, or prop
    const actualQueueId = queueId || position?.queueId || queue?._id;
    
    if (!actualQueueId || actualQueueId === 'null') {
      console.warn('Cannot load position: queueId is not available');
      return;
    }
    
    setIsUpdatingPosition(true);
    
    try {
      let positionData = null;
      
      // Always use public methods for participants (we don't have authenticated users here)
      // Public user - reload the entry by ID if we have it
      const entryIdToLoad = position?.id || position?._id || entryId;
      if (entryIdToLoad) {
        positionData = await queueApiService.getEntryById(entryIdToLoad);
      } else {
        // Try to get from session storage
        const storedEntry = sessionStorage.getItem(`queue_entry_${actualQueueId}`);
        if (storedEntry) {
          const entryInfo = JSON.parse(storedEntry);
          if (entryInfo.entryId) {
            // If we have entryId in storage, use it
            positionData = await queueApiService.getEntryById(entryInfo.entryId);
          } else if (entryInfo.phone) {
            // Fall back to public position with tempUserId
            const tempUserId = generateTempUserId(entryInfo.phone);
            positionData = await queueApiService.getPositionPublic(actualQueueId, tempUserId);
          }
        }
      }
      
      if (positionData) {
        setPosition(positionData);
        setQrCodeData(positionData.qrCode || '');
      }
    } catch (err) {
      console.error('Error loading position:', err);
      setError('Não foi possível carregar sua posição na fila');
    } finally {
      // Add a small delay to make the loading visible
      setTimeout(() => {
        setIsUpdatingPosition(false);
      }, 500);
    }
  };

  const handlePositionUpdate = (data: any) => {
    console.log('Position update received in ParticipantQueueView:', data);
    
    // Get the current user identifier (could be userId or from position data)
    const currentUserId = userId || position?.userId;
    const currentEntryId = entryId || position?.id || position?._id;
    
    // Handle both direct userId match and updates array format
    let userUpdate = null;
    
    // Check for direct match
    if (currentUserId && data.userId === currentUserId) {
      userUpdate = data;
    } else if (currentEntryId && (data.entryId === currentEntryId || data._id === currentEntryId)) {
      userUpdate = data;
    } else if (data.updates && Array.isArray(data.updates)) {
      // Check in updates array
      userUpdate = data.updates.find((update: any) => {
        return (currentUserId && update.userId === currentUserId) ||
               (currentEntryId && (update.entryId === currentEntryId || update._id === currentEntryId));
      });
    }
    
    if (userUpdate) {
      console.log('Updating position for entry:', currentEntryId, 'new position:', userUpdate.position);
      
      setPosition(prev => prev ? {
        ...prev,
        position: userUpdate.position !== undefined ? userUpdate.position : prev.position,
        estimatedWaitTime: userUpdate.estimatedTime || userUpdate.estimatedWaitTime || prev.estimatedWaitTime,
        peopleAhead: userUpdate.peopleAhead !== undefined ? userUpdate.peopleAhead : prev.peopleAhead
      } : null);
      
      if (userUpdate.position <= 3) {
        setIsNearTurn(true);
      } else {
        setIsNearTurn(false);
      }
    } else {
      console.log('No matching update found for current user/entry');
    }
  };

  const handleTurnSoon = (data: any) => {
    if (data.userId === userId) {
      setIsNearTurn(true);
      // Show notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Sua vez está chegando!', {
          body: data.message,
          icon: '/icon-192x192.png'
        });
      }
    }
  };

  const handleTurnNow = (data: any) => {
    if (data.userId === userId) {
      setIsNearTurn(true);
      // Show notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('É sua vez!', {
          body: data.message,
          icon: '/icon-192x192.png'
        });
      }
    }
  };

  const generateTempUserId = (phone: string | null | undefined): string => {
    // Generate a consistent temporary user ID based on phone number
    if (!phone) {
      console.warn('[generateTempUserId] Phone is null/undefined, using fallback');
      return `temp_unknown_${Date.now()}`;
    }
    return `temp_${phone.replace(/\D/g, '')}`;
  };

  const handleLeaveQueue = async () => {
    // Get the actual queueId from position or queue state if not provided
    const actualQueueId = queueId || position?.queueId || queue?._id;
    
    if (!actualQueueId) {
      setError('Não foi possível sair da fila: ID da fila não encontrado');
      return;
    }
    
    // Get entryId from various sources
    const currentEntryId = entryId || position?.id || position?._id;
    
    if (!currentEntryId) {
      // Try to get from session storage as fallback
      const storedEntry = sessionStorage.getItem(`queue_entry_${actualQueueId}`);
      if (storedEntry) {
        const entryInfo = JSON.parse(storedEntry);
        if (entryInfo.entryId) {
          const tempUserId = `entry_${entryInfo.entryId}`;
          try {
            await queueApiService.leaveQueuePublic(actualQueueId, tempUserId);
            sessionStorage.removeItem(`queue_entry_${actualQueueId}`);
            alert('Você saiu da fila com sucesso! Obrigado por usar nosso serviço.');
            
            if (onLeaveQueue) {
              onLeaveQueue();
            } else {
              setViewState('queue-info');
              setPosition(null);
            }
            return;
          } catch (err: any) {
            setError(err.response?.data?.message || 'Não foi possível sair da fila');
            return;
          }
        }
      }
      
      setError('Não foi possível identificar sua entrada na fila. Por favor, recarregue a página.');
      return;
    }
    
    try {
      // Use entryId directly - this is the most reliable approach
      const tempUserId = `entry_${currentEntryId}`;
      console.log('[handleLeaveQueue] Calling API with:', { actualQueueId, tempUserId, currentEntryId });
      await queueApiService.leaveQueuePublic(actualQueueId, tempUserId);
      
      // Clear session storage
      sessionStorage.removeItem(`queue_entry_${actualQueueId}`);
      
      // Set flags to show exit modal
      setHasLeftQueue(true);
      setShowExitModal(true);
      setPosition(null);
      
      // Don't navigate away, keep user on the same page
    } catch (err: any) {
      console.error('Error leaving queue:', err);
      setError(err.response?.data?.message || 'Não foi possível sair da fila');
    }
  };

  const formatWaitTime = (minutes?: number) => {
    if (minutes === undefined || minutes === null) return 'Calculando...';
    if (minutes === 0) return 'Próximo';
    if (minutes < 60) return `${minutes} minutos`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  };

  // Check if queue has scheduled start time
  const hasScheduledStart = () => {
    return queue?.settings?.serviceStartTime != null;
  };

  // Check if service has started
  const isServiceStarted = () => {
    if (!hasScheduledStart()) return true;
    const startTime = new Date(queue!.settings!.serviceStartTime!);
    return new Date() >= startTime;
  };

  // Format service start time
  const formatServiceStartTime = () => {
    if (!hasScheduledStart()) return null;
    const startTime = new Date(queue!.settings!.serviceStartTime!);
    return startTime.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/Sao_Paulo' // Timezone explícito
    });
  };

  // Calculate time until service starts
  const getTimeUntilStart = () => {
    if (!hasScheduledStart() || isServiceStarted()) return null;
    const startTime = new Date(queue!.settings!.serviceStartTime!);
    const now = new Date();
    const diffMs = startTime.getTime() - now.getTime();
    const diffMinutes = Math.ceil(diffMs / 60000);
    
    if (diffMinutes < 60) {
      return `${diffMinutes} minutos`;
    } else {
      const hours = Math.floor(diffMinutes / 60);
      const mins = diffMinutes % 60;
      return `${hours}h ${mins}min`;
    }
  };

  // Calculate estimated service time for position
  const calculateEstimatedServiceTime = (position: number) => {
    if (!queue?.settings) return null;

    const avgTime = queue.settings.estimatedServiceTime || 5;
    const simultaneousAttendance = queue.settings.simultaneousAttendance || 1;

    // Calculate attendance group (same logic as backend)
    const attendanceGroup = Math.ceil(position / simultaneousAttendance);
    const minutesToService = (attendanceGroup - 1) * avgTime;


    
    if (hasScheduledStart()) {
      const startTime = new Date(queue.settings.serviceStartTime!);
      const estimatedTime = new Date(startTime.getTime() + minutesToService * 60000);
      
      // If service hasn't started, calculate from start time
      if (!isServiceStarted()) {
        return estimatedTime.toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: 'America/Sao_Paulo' // Timezone explícito
        });
      }
    }
    
    // Service already started or no scheduled start
    const now = new Date();
    const estimatedTime = new Date(now.getTime() + minutesToService * 60000);
    return estimatedTime.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/Sao_Paulo' // Timezone explícito
    });
  };

  // Removed unused utility functions

  const textColor = config?.corTexto || '#FFFFFF';

  // Loading state
  if (viewState === 'loading') {
    return (
      <div className="min-h-screen">
        <div
          className="min-h-screen bg-cover bg-center bg-no-repeat relative"
          style={{ backgroundImage: `url(${config?.imagemFundo || '/idVIsual.webp'})` }}
        >
          <div className="absolute inset-0 bg-scrim/40" />
          <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-border"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (viewState === 'error' || (error && viewState !== 'registration')) {
    return (
      <div className="min-h-screen">
        <div
          className="min-h-screen bg-cover bg-center bg-no-repeat relative"
          style={{ backgroundImage: `url(${config?.imagemFundo || '/idVIsual.webp'})` }}
        >
          <div className="absolute inset-0 bg-scrim/40" />
          <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
            <div className="w-full max-w-xl rounded-2xl border border-border bg-scrim/35 backdrop-blur-md shadow-2xl p-6 md:p-8" style={{ color: textColor }}>
              {config?.logo && (
                <div className="flex justify-center mb-6">
                  <img src={config.logo} alt="logo" className="h-10 object-contain" />
                </div>
              )}
              <div className="text-center">
                <AlertCircle className="mx-auto mb-4" size={48} style={{ color: textColor }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: textColor }}>Erro</h3>
                <p className="mb-4" style={{ color: textColor }}>{error}</p>
                <button
                  onClick={initializeView}
                  className="rounded-full px-6 py-3 font-semibold shadow-lg"
                  style={{ background: config.corPrincipal, color: config.corTexto }}
                >
                  Tentar Novamente
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Queue info state (before registration)
  if (viewState === 'queue-info') {
    return (
      <div className="min-h-screen">
        <div
          className="min-h-screen bg-cover bg-center bg-no-repeat relative"
          style={{ backgroundImage: `url(${config?.imagemFundo || '/idVIsual.webp'})` }}
        >
          <div className="absolute inset-0 bg-scrim/40" />
          <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
            <div className="w-full max-w-xl rounded-2xl border border-border bg-scrim/35 backdrop-blur-md shadow-2xl p-6 md:p-8" style={{ color: textColor }}>
              {config?.logo && (
                <div className="flex justify-center mb-6">
                  <img src={config.logo} alt="logo" className="h-10 object-contain" />
                </div>
              )}
              
              <h1 className="text-2xl font-bold mb-2 text-center" style={{ color: textColor }}>
                {queue?.name || 'Fila'}
              </h1>

              {/* Priority / Scheduling Information Message */}
              <p className="text-sm text-center mb-6 opacity-90" style={{ color: textColor }}>
                {config.modoAgendamento
                  ? 'Você pode se inscrever e garantir sua participação!'
                  : 'Caso você tenha direito ao atendimento prioritário, dirija-se a entrada do stand.'}
              </p>

              {/* Queue Status */}
              <div className="flex justify-center mb-6">
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  queue?.status === 'active' ? 'bg-brand-green/20 text-status-ok' : 
                  queue?.status === 'paused' ? 'bg-brand-attention/20 text-status-attention' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {queue?.status === 'active' ? 'Ativa' : 
                   queue?.status === 'paused' ? 'Pausada' : 
                   queue?.status === 'completed' ? 'Concluída' : 'Inativa'}
                </div>
              </div>
              
              {/* Service Start Time Info - NEW */}
              {hasScheduledStart() && !isServiceStarted() && (
                <div className="mb-6 p-4 rounded-ds-card bg-primary/20 border border-primary/30">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-primary font-semibold">Atendimento agendado</p>
                  </div>
                  <p className="text-center" style={{ color: textColor }}>
                    O atendimento começará às <span className="font-bold">{formatServiceStartTime()}</span>
                  </p>
                  {!config.modoAgendamento && (
                    <p className="text-center text-sm mt-1" style={{ color: textColor, opacity: 0.8 }}>
                      Tempo restante: {getTimeUntilStart()}
                    </p>
                  )}
                  <p className="text-center text-xs mt-2" style={{ color: textColor, opacity: 0.7 }}>
                    {config.modoAgendamento
                      ? 'Você pode realizar seu agendamento agora.'
                      : 'Você pode entrar na fila agora e garantir sua posição!'}
                  </p>
                </div>
              )}

              {/* Queue Metrics */}
              {!config.modoAgendamento && <div className="flex items-center justify-center gap-8 mb-6">
                <div className="text-center">
                  <div className="text-sm mb-1" style={{ color: textColor }}>Pessoas na Fila</div>
                  <div className="text-3xl font-bold" style={{ color: textColor }}>
                    {queue?.metrics?.currentSize || 0}
                    {queue?.settings?.maxSize && (
                      <span className="text-lg ml-1">/ {queue.settings.maxSize}</span>
                    )}
                  </div>
                  {queue?.settings?.maxSize && (
                    <div className="mt-2">
                      <div className="w-32 h-2 bg-foreground/20 rounded-full overflow-hidden mx-auto">
                        <div 
                          className={`h-full transition-all ${
                            (queue.metrics?.currentSize || 0) / queue.settings.maxSize >= 0.95 
                              ? 'bg-brand-danger' 
                              : (queue.metrics?.currentSize || 0) / queue.settings.maxSize >= 0.8 
                              ? 'bg-brand-attention' 
                              : 'bg-brand-green'
                          }`}
                          style={{ width: `${Math.min(100, ((queue.metrics?.currentSize || 0) / queue.settings.maxSize) * 100)}%` }}
                        />
                      </div>
                      {queue.metrics && queue.settings.maxSize - queue.metrics.currentSize > 0 && (
                        <p className="text-xs mt-1" style={{ color: textColor }}>
                          {queue.settings.maxSize - queue.metrics.currentSize} vagas restantes
                        </p>
                      )}
                      {queue.metrics && queue.settings.maxSize - queue.metrics.currentSize <= 0 && (
                        <p className="text-xs mt-1 text-status-danger">
                          Fila lotada
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="h-12 w-px bg-foreground/50" />
                <div className="text-center">
                  <div className="text-sm mb-1" style={{ color: textColor }}>
                    {hasScheduledStart() && !isServiceStarted() ? 'Início do Atendimento' : 'Tempo Médio'}
                  </div>
                  <div className="text-3xl font-bold" style={{ color: textColor }}>
                    {hasScheduledStart() && !isServiceStarted() ? (
                      <span>{formatServiceStartTime()}</span>
                    ) : (
                      <>
                        {queue?.settings?.estimatedServiceTime || 5}
                        <span className="text-lg ml-1">min</span>
                      </>
                    )}
                  </div>
                </div>
              </div>}

              {/* Call to action */}
              {queue?.status === 'active' ? (
                <div className="space-y-4">
                  {queue?.settings?.maxSize && queue?.metrics?.currentSize && 
                   queue.metrics.currentSize >= queue.settings.maxSize ? (
                    <>
                      <button
                        disabled
                        className="w-full rounded-full px-6 py-3 font-semibold shadow-lg opacity-60 cursor-not-allowed"
                        style={{ background: config.corPrincipal, color: config.corTexto }}
                      >
                        Fila Lotada
                      </button>
                      <p className="text-sm text-center text-status-danger">
                        A fila atingiu sua capacidade máxima de {queue.settings.maxSize} participantes
                      </p>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={handleJoinQueue}
                        className="w-full rounded-full px-6 py-3 font-semibold shadow-lg"
                        style={{ background: config.corPrincipal, color: config.corTexto }}
                      >
                        {config.modoAgendamento ? 'Agendar' : 'Entrar na Fila'}
                      </button>

                      <p className="text-sm text-center" style={{ color: textColor }}>
                        Você receberá notificações por SMS sobre sua posição na fila
                      </p>

                      {config.modoAgendamento && hasScheduledStart() ? (
                        <div className="mt-3 p-3 rounded-ds-card bg-brand-attention/10 border border-brand-attention/30">
                          <p className="text-sm text-center text-status-attention">
                            ⚠️ Atenção: O Agendamento começará às {formatServiceStartTime()}h. Chegue 10 minutos antes, a tolerância máxima é de 15 minutos.
                          </p>
                        </div>
                      ) : hasScheduledStart() && !isServiceStarted() ? (
                        <div className="mt-3 p-3 rounded-ds-card bg-brand-attention/10 border border-brand-attention/30">
                          <p className="text-sm text-center text-status-attention">
                            ⏰ Atenção: O atendimento começará às {formatServiceStartTime()}
                          </p>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              ) : queue?.status === 'completed' ? (
                <div className="text-center space-y-4">
                  <div className="mx-auto w-16 h-16 bg-brand-green/20 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-status-ok" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold" style={{ color: textColor }}>
                    Fila Finalizada
                  </h3>
                  <p style={{ color: textColor }}>
                    Esta fila foi concluída com sucesso. Todos os participantes foram atendidos.
                  </p>
                  <p className="text-sm opacity-75" style={{ color: textColor }}>
                    Obrigado pela sua participação!
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <p style={{ color: textColor }}>
                    {queue?.status === 'paused' ? 'A fila está temporariamente pausada' :
                     'Esta fila não está aceitando novos participantes'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Registration form state
  if (viewState === 'registration') {
    return (
      <div className="min-h-screen">
        <div
          className="min-h-screen bg-cover bg-center bg-no-repeat relative"
          style={{ backgroundImage: `url(${config?.imagemFundo || '/idVIsual.webp'})` }}
        >
          <div className="absolute inset-0 bg-scrim/40" />
          <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
            <div className="w-full max-w-xl rounded-2xl border border-border bg-scrim/35 backdrop-blur-md shadow-2xl p-6 md:p-8" style={{ color: textColor }}>
              {config?.logo && (
                <div className="flex justify-center mb-6">
                  <img src={config.logo} alt="logo" className="h-10 object-contain" />
                </div>
              )}
              
              <h1 className="text-2xl font-bold mb-2 text-center" style={{ color: textColor }}>
                Olá, que bom te ver aqui.
              </h1>
              <p className="mb-6 text-center" style={{ color: textColor }}>
                Preencha seus dados para continuar.
              </p>
              
              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 rounded-ds-card bg-brand-danger/20 border border-brand-danger/50">
                  <p className="text-status-danger text-sm">{error}</p>
                </div>
              )}
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleRegistrationSubmit)} className="space-y-4">
                  {/* Name field */}
                  <div>
                    <label className="block mb-1 text-sm" style={{ color: textColor }}>Nome</label>
                    <input
                      {...form.register('name')}
                      placeholder="Seu nome completo"
                      className="w-full px-4 py-2 rounded-xl bg-scrim/50 border border-border outline-none focus:ring-2 focus:ring-border placeholder-muted-foreground"
                      style={{ color: 'var(--text-on-dark)' }}
                      required
                    />
                    {form.formState.errors.name && (
                      <span className="text-xs text-status-danger mt-1">
                        {form.formState.errors.name.message as string}
                      </span>
                    )}
                  </div>

                  {/* Phone field */}
                  <div>
                    <label className="block mb-1 text-sm" style={{ color: textColor }}>Telefone</label>
                    <input
                      {...form.register('phone')}
                      onChange={(e) => {
                        // Apply Brazilian phone mask
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length > 11) value = value.slice(0, 11);

                        let formatted = value;
                        if (value.length > 0) {
                          if (value.length <= 2) {
                            formatted = `(${value}`;
                          } else if (value.length <= 7) {
                            formatted = `(${value.slice(0, 2)}) ${value.slice(2)}`;
                          } else if (value.length <= 11) {
                            const firstPart = value.slice(2, 7);
                            const secondPart = value.slice(7);
                            formatted = `(${value.slice(0, 2)}) ${firstPart}${secondPart ? '-' + secondPart : ''}`;
                          }
                        }

                        form.setValue('phone', formatted);
                      }}
                      placeholder="(11) 99999-9999"
                      className="w-full px-4 py-2 rounded-xl bg-scrim/50 border border-border outline-none focus:ring-2 focus:ring-border placeholder-muted-foreground"
                      style={{ color: 'var(--text-on-dark)' }}
                      maxLength={15}
                      required
                    />
                    {form.formState.errors.phone && (
                      <span className="text-xs text-status-danger mt-1">
                        {form.formState.errors.phone.message as string}
                      </span>
                    )}
                  </div>

                  {/* Email field */}
                  <div>
                    <label className="block mb-1 text-sm" style={{ color: textColor }}>Email</label>
                    <input
                      type="email"
                      {...form.register('email')}
                      placeholder="seu@email.com"
                      className="w-full px-4 py-2 rounded-xl bg-scrim/50 border border-border outline-none focus:ring-2 focus:ring-border placeholder-muted-foreground"
                      style={{ color: 'var(--text-on-dark)' }}
                    />
                    {form.formState.errors.email && (
                      <span className="text-xs text-status-danger mt-1">
                        {form.formState.errors.email.message as string}
                      </span>
                    )}
                  </div>

                  {/* Dynamic Custom Fields */}
                  {customFields.length > 0 && (
                    <div className="space-y-4">
                      <div className="border-t border-border pt-4">
                        <h3 className="text-sm font-semibold mb-3" style={{ color: textColor }}>
                          Informações Adicionais
                        </h3>
                        <div className="[&_.form-label]:text-on-dark [&_.form-message]:text-status-danger [&_input]:bg-scrim/50 [&_input]:border-border [&_input]:text-on-dark [&_textarea]:bg-scrim/50 [&_textarea]:border-border [&_textarea]:text-on-dark [&_select]:bg-scrim/50 [&_select]:border-border [&_select]:text-on-dark">
                          <DynamicFieldList
                            fields={customFields}
                            form={form}
                            disabled={isSubmitting}
                            formValues={watchedCustomFields}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                
                {/* Dynamic Opt-ins */}
                <div className="space-y-3 mt-2">
                  {[...optinsConfig]
                    .sort((a, b) => a.order - b.order)
                    .map((optin) => {
                      // Enrich usage_terms opt-in with config.termosUso if content is empty
                      const enrichedOptin =
                        optin.key === 'usage_terms' &&
                        optin.type === OptinType.TEXT &&
                        !optin.content &&
                        config?.termosUso
                          ? { ...optin, content: config.termosUso }
                          : optin;

                      return (
                        <OptinCheckbox
                          key={optin.key}
                          optin={enrichedOptin}
                          checked={optinValues[optin.key] || false}
                          onChange={(v) =>
                            setOptinValues((prev) => ({ ...prev, [optin.key]: v }))
                          }
                          textColor={textColor}
                        />
                      );
                    })}
                </div>
                
                {error && (
                  <div className="bg-brand-danger/20 border border-brand-danger/30 rounded-ds-card p-3 text-center">
                    <span style={{ color: textColor }}>{error}</span>
                  </div>
                )}
                
                  <button
                    type="submit"
                    disabled={
                      optinsConfig.filter((o) => o.required).some((o) => !optinValues[o.key]) ||
                      isSubmitting ||
                      !form.formState.isValid
                    }
                    className="w-full mt-4 rounded-full px-6 py-3 font-semibold shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ background: config.corPrincipal, color: config.corTexto }}
                  >
                    {isSubmitting ? 'Entrando...' : 'Entrar'}
                  </button>

                <button
                  type="button"
                  onClick={() => setViewState('queue-info')}
                  className="w-full rounded-full px-6 py-3 font-semibold mt-3 border-2"
                  style={{ color: textColor, borderColor: textColor, background: 'transparent' }}
                >
                  Voltar
                </button>
                </form>
              </Form>
            </div>
          </div>
        </div>
        
        {/* Opt-in dialogs are handled by OptinCheckbox components */}
      </div>
    );
  }

  // Position tracking state
  if (viewState === 'tracking') {
    // If we don't have position or queue data yet, show loading
    // But don't show loading if user has left the queue (abandoned status or hasLeftQueue flag)
    if ((!position || !queue) && !hasLeftQueue && position?.status !== 'abandoned') {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando informações da fila...</p>
          </div>
        </div>
      );
    }
    return (
      <>
      <div className="min-h-screen">
        <div
          className="min-h-screen bg-cover bg-center bg-no-repeat relative"
          style={{ backgroundImage: `url(${config?.imagemFundo || '/idVIsual.webp'})` }}
        >
          <div className="absolute inset-0 bg-scrim/40" />
          <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
            <div className="w-full max-w-xl rounded-2xl border border-border bg-scrim/35 backdrop-blur-md shadow-2xl p-6 md:p-8" style={{ color: textColor }}>
              {config?.logo && (
                <div className="flex justify-center mb-6">
                  <img src={config.logo} alt="logo" className="h-10 object-contain" />
                </div>
              )}
              
              <h1 className="text-2xl font-bold mb-2 text-center" style={{ color: textColor }}>
                {position?.status === 'completed' ? 'Atendimento concluído!' : 
                 position?.status === 'abandoned' ? 'Você saiu da fila' :
                 queue?.status === 'completed' ? 'Fila Finalizada' :
                 'Obrigado pela sua participação!'}
              </h1>
              <p className="text-center mb-6" style={{ color: textColor }}>
                {queue?.status === 'completed' && position?.status !== 'completed' ? 
                  'Esta fila foi finalizada. Agradecemos sua participação!' :
                  position ? getStatusContextualMessage(position.status || 'waiting', position.position) : ''}
              </p>
              
              {/* Status Badge */}
              <div className="flex justify-center mb-6">
                <div className={`px-4 py-2 rounded-full font-medium border ${
                  queue?.status === 'completed' ? 'bg-muted text-muted-foreground border-border' :
                  getStatusDisplay(position?.status || 'waiting').tailwindClasses
                }`}>
                  {queue?.status === 'completed' ? 'Fila Finalizada' : 
                   getStatusDisplay(position?.status || 'waiting').labelPtBr}
                </div>
              </div>
              
              {/* Position Display - only show for waiting status and active queue, hidden in modoAgendamento */}
              {position?.status === 'waiting' && queue?.status !== 'completed' && !config.modoAgendamento && (
                <div className="flex items-center justify-center gap-8 mb-6">
                  <div className="text-center">
                    <div className="text-sm" style={{ color: textColor }}>Posição</div>
                    <div className="text-5xl font-extrabold" style={{ color: textColor }}>
                      #{position?.position || 0}
                    </div>
                  </div>
                  <div className="h-12 w-px bg-foreground/50" />
                  <div className="text-center">
                    <div className="text-sm" style={{ color: textColor }}>
                      {position?.isScheduled || hasScheduledStart() ? 'Horário Estimado' : 'Estimativa'}
                    </div>
                    <div className="text-3xl font-bold" style={{ color: textColor }}>
                      {/* Use formatted time from backend if available */}
                      {position?.formattedTime ? (
                        position.formattedTime
                      ) : hasScheduledStart() ? (
                        calculateEstimatedServiceTime(position?.position || 0) || formatWaitTime(position?.estimatedWaitTime || 0)
                      ) : (
                        formatWaitTime(position?.estimatedWaitTime || 0)
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Simultaneous Attendance Display - show when queue has simultaneous attendance */}
              {queue?.simultaneousAttendance && queue.simultaneousAttendance > 1 && (
                <div className="mb-6">
                  <SimultaneousAttendanceDisplay
                    currentlyServing={queue.currentlyServing || 0}
                    maxSimultaneous={queue.simultaneousAttendance}
                    servingEntries={queue.servingEntries || []}
                    estimatedWaitTime={position?.estimatedWaitTime}
                    isYourTurn={position?.status === 'called' || position?.status === 'serving'}
                    className=""
                  />
                </div>
              )}

              {/* Tracking URL - More Subtle */}
              {trackingUrl && (
                <div className="mb-4 flex justify-center">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(trackingUrl);
                      setLinkCopied(true);
                      setTimeout(() => setLinkCopied(false), 2000);
                    }}
                    className="relative inline-flex items-center gap-2 px-4 py-2 text-xs rounded-full bg-foreground/10 hover:bg-foreground/15 transition-all duration-200"
                    style={{ color: textColor }}
                  >
                    {linkCopied ? (
                      <>
                        <svg className="w-4 h-4 text-status-ok" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-status-ok">Link copiado!</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        <span className="opacity-90">Copiar link</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Service Start Time Warning - NEW */}
              {hasScheduledStart() && !isServiceStarted() && position?.status === 'waiting' && (
                <div className="mb-4 p-3 rounded-ds-card bg-brand-attention/10 border border-brand-attention/30">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 mt-0.5 text-status-attention flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"/>
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-status-attention">Atendimento ainda não iniciado</p>
                      <p className="text-sm" style={{ color: textColor, opacity: 0.9 }}>
                        O atendimento começará às {formatServiceStartTime()}. Seu horário estimado é às {calculateEstimatedServiceTime(position?.position || 0)}.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* SMS Notification Info - hide when queue is completed */}
              {queue?.status !== 'completed' && (
                <div className="mb-4 p-3 rounded-ds-card bg-foreground/5 border border-border">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 mt-0.5 text-status-ok flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"/>
                    </svg>
                    <p className="text-sm" style={{ color: textColor, opacity: 0.9 }}>
                      {config.modoAgendamento
                        ? 'Enviamos uma mensagem SMS para o número cadastrado para você acompanhar seu agendamento.'
                        : 'Enviamos uma mensagem SMS para o número cadastrado para você acompanhar sua posição na fila.'}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Action Buttons - hide when queue is completed */}
              {queue?.status !== 'completed' && (
                <div className="space-y-3">
                  <button
                    onClick={loadPosition}
                    disabled={isUpdatingPosition}
                    className="w-full rounded-full px-6 py-3 font-semibold border-2 transition-all duration-200 flex items-center justify-center gap-2"
                    style={{ 
                      color: textColor, 
                      borderColor: textColor, 
                      background: 'transparent',
                      opacity: isUpdatingPosition ? 0.7 : 1
                    }}
                  >
                    {isUpdatingPosition ? (
                      <>
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Atualizando...</span>
                      </>
                    ) : (
                      'Atualizar Posição'
                    )}
                  </button>
                  
                  {position?.status === 'waiting' && (
                    <button
                      onClick={() => setShowConfirmCancelModal(true)}
                      className="w-full rounded-full px-6 py-3 font-semibold border-2 border-brand-danger text-status-danger bg-transparent hover:bg-brand-danger/20 transition-colors"
                    >
                      Cancelar minha participação
                    </button>
                  )}
                </div>
              )}
              
              {/* Message for completed queue */}
              {queue?.status === 'completed' && (
                <div className="mt-6 p-4 rounded-ds-card bg-muted border border-border/30">
                  <p className="text-center text-sm" style={{ color: textColor, opacity: 0.9 }}>
                    Esta fila foi encerrada. Não é mais possível realizar alterações ou atualizações.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Cancel Confirmation Modal */}
      {showConfirmCancelModal && (
        <ModalPortal><div className="fixed inset-0 bg-scrim/80 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md rounded-2xl border border-border bg-scrim/85 backdrop-blur-md shadow-2xl p-6">
            <div className="text-center" style={{ color: textColor }}>
              <div className="mx-auto w-16 h-16 bg-brand-danger/20 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-status-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              <h3 className="text-xl font-semibold mb-2" style={{ color: textColor }}>
                Confirmar cancelamento
              </h3>
              <p className="mb-6 opacity-90" style={{ color: textColor }}>
                {config.modoAgendamento
                  ? 'Tem certeza que deseja cancelar seu agendamento? Você perderá sua reserva.'
                  : 'Tem certeza que deseja sair da fila? Você perderá sua posição atual e precisará entrar novamente no final da fila.'}
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmCancelModal(false)}
                  className="flex-1 rounded-full px-6 py-3 font-semibold border-2 transition-colors"
                  style={{ 
                    color: textColor, 
                    borderColor: textColor, 
                    background: 'transparent'
                  }}
                >
                  Voltar
                </button>
                
                <button
                  onClick={() => {
                    setShowConfirmCancelModal(false);
                    handleLeaveQueue();
                  }}
                  className="flex-1 rounded-full px-6 py-3 font-semibold bg-brand-danger text-destructive-foreground hover:bg-brand-danger transition-colors"
                >
                  Sair da fila
                </button>
              </div>
            </div>
          </div>
        </div></ModalPortal>
      )}
      
      {/* Exit Confirmation Modal */}
      {showExitModal && (
        <ModalPortal><div className="fixed inset-0 bg-scrim/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 max-w-md w-full">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-brand-green/10 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-status-ok" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h3 className="text-xl font-semibold mb-2">Você saiu da fila</h3>
              <p className="text-muted-foreground mb-6">
                Sua participação na fila foi cancelada com sucesso. Obrigado por usar nosso serviço!
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowExitModal(false);
                    setViewState('queue-info');
                    setHasLeftQueue(false);
                  }}
                  className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary transition-colors"
                >
                  {config.modoAgendamento ? 'Agendar novamente' : 'Entrar na fila novamente'}
                </button>
                
                <button
                  onClick={() => {
                    setShowExitModal(false);
                  }}
                  className="w-full px-6 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div></ModalPortal>
      )}
      </>
    );
  }

  // Fallback state
  return (
    <>
      <div className="text-center py-12">
        <AlertCircle className="mx-auto mb-4 text-muted-foreground" size={48} />
        <p className="text-muted-foreground">Estado de visualização inválido</p>
        <button
          onClick={initializeView}
          className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary transition-colors"
        >
          Recarregar
        </button>
      </div>
      
      {/* Exit Confirmation Modal */}
      {showExitModal && (
        <ModalPortal><div className="fixed inset-0 bg-scrim/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 max-w-md w-full">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-brand-green/10 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-status-ok" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h3 className="text-xl font-semibold mb-2">Você saiu da fila</h3>
              <p className="text-muted-foreground mb-6">
                Sua participação na fila foi cancelada com sucesso. Obrigado por usar nosso serviço!
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowExitModal(false);
                    setViewState('queue-info');
                  }}
                  className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary transition-colors"
                >
                  {config.modoAgendamento ? 'Agendar novamente' : 'Entrar na fila novamente'}
                </button>
                
                <button
                  onClick={() => {
                    setShowExitModal(false);
                    // Optional: navigate to home or keep on same page
                  }}
                  className="w-full px-6 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div></ModalPortal>
      )}
    </>
  );
}